import type {
  ClientImpactReportDeferred,
  ClientImpactReportJob,
  ImpactReport,
  ImpactReportResponse,
  ReportFilter,
} from "@/interfaces/impactReport";
import { apiClient } from "@/lib/apiClient";
import {
  buildReportFilterQueryString,
  describeImpactReportDeferralGuardFailure,
  isClientImpactReportDeferred,
} from "@/utils/impact-report-helper";

/**
 * Fetches the client impact report for the given filter. `apiClient`'s request interceptor
 * already attaches the Clerk bearer token, so no auth handling here.
 *
 * The filter's query-string encoding lives in `utils/impact-report-helper.ts`, driven by the
 * exhaustive field map in `constants/impactReport.ts` — this file deliberately enumerates no
 * filter fields of its own.
 *
 * A 202 means the report was deferred to a background job (it will be emailed); its body is the
 * deferral, not a report. Branched on the HTTP status rather than on the body's shape — but the
 * body still has to PASS the shape check, `isClientImpactReportDeferred`, before it is trusted as
 * one: a 202 whose body is missing `jobId`/`recipient` is not a usable deferral, and returning it
 * anyway would have `useImpactReportJob` poll an empty jobId forever, rendering an infinite
 * "preparing" spinner with no way out. Logged (never the recipient email — see
 * `describeImpactReportDeferralGuardFailure`) and thrown instead, so `useImpactReport`'s query
 * lands in its error state, which the page already renders with a retry.
 */
export async function fetchClientImpactReport(filter: ReportFilter): Promise<ImpactReportResponse> {
  const query = buildReportFilterQueryString(filter);
  const response = await apiClient.get<ImpactReport | ClientImpactReportDeferred>(
    `/reports/client-impact-report${query ? `?${query}` : ""}`,
  );
  if (response.status === 202) {
    const body = response.data;
    if (!isClientImpactReportDeferred(body)) {
      console.error("fetchClientImpactReport: 202 response failed the deferral guard", {
        missingOrInvalidFields: describeImpactReportDeferralGuardFailure(body),
      });
      throw new Error(
        "fetchClientImpactReport: malformed 202 deferral body (see console for which fields failed)",
      );
    }
    return body;
  }
  return response.data as ImpactReport;
}

/**
 * Fetches a deferred report's job. A 404 (`REPORT_JOB_NOT_FOUND`) means the job expired, never
 * existed, or belongs to another user.
 */
export async function fetchClientImpactReportJob(jobId: string): Promise<ClientImpactReportJob> {
  const { data } = await apiClient.get<ClientImpactReportJob>(
    `/reports/client-impact-report/jobs/${encodeURIComponent(jobId)}`,
  );
  return data;
}
